// frontend/src/utils/teams.ts

// Mapping of full team names (as expected from backend/API) to abbreviations
export const teamAbbreviations: { [key: string]: string } = {
    "Arsenal": "ARS",
    "Aston Villa": "AVL",
    "AFC Bournemouth": "BOU",
    "Brentford": "BRE",
    "Brighton & Hove Albion": "BHA",
    "Burnley": "BUR",
    "Chelsea": "CHE",
    "Coventry City": "COV",
    "Crystal Palace": "CRY",
    "Everton": "EVE",
    "Fulham": "FUL",
    "Hull City": "HUL",
    "Ipswich Town": "IPS",
    "Leeds United": "LEE",
    "Leicester City": "LEI",
    "Liverpool": "LIV",
    "Manchester City": "MCI",
    "Manchester United": "MUN",
    "Newcastle United": "NEW",
    "Nottingham Forest": "NFO",
    "Southampton": "SOU",
    "Sunderland": "SUN",
    "Tottenham Hotspur": "TOT",
    "West Ham United": "WHU",
    "Wolverhampton Wanderers": "WOL",

    // --- UPDATED MLS Teams ---
    // Keys now match the StatsBomb API names you provided.
    // Abbreviations are kept if they were standard, or updated if needed.
    "LAFC": "LAFC",
    "LA Galaxy": "LAG",
    "Cincinnati": "CIN", // Assuming "FC Cincinnati" might be truncated to "Cincinnati" by StatsBomb
    "New York Red Bulls": "RBNY",
    "Charlotte": "CLT", // Assuming "Charlotte FC" might be truncated
    "DC United": "DCU", // Assuming "D.C. United" might be truncated
    "Toronto FC": "TOR",
    "San Diego FC": "SD", // Or "SDFC" - choose a common one
    "Chicago Fire": "CHI", // Assuming "Chicago Fire FC" might be truncated
    "Real Salt Lake": "RSL",
    "Houston Dynamo": "HOU", // Assuming "Houston Dynamo FC" might be truncated
    "Colorado Rapids": "COL",
    "Columbus Crew": "CLB",
    "Atlanta United": "ATL", // Assuming "Atlanta United FC" might be truncated
    "New York City FC": "NYC",
    "CF Montreal": "MTL", // Matches your existing "CF Montréal" key if that's what SB sends
    "New England Revolution": "NER",
    "Seattle Sounders": "SEA", // Assuming "Seattle Sounders FC" might be truncated
    "Inter Miami": "MIA", // Assuming "Inter Miami CF" might be truncated
    "Orlando City": "ORL", // Assuming "Orlando City SC" might be truncated
    "Portland Timbers": "POR",
    "St. Louis City": "STL", // Assuming "St. Louis City SC" might be truncated
    "FC Dallas": "DAL",
    "Sporting Kansas City": "SKC",
    "Vancouver Whitecaps": "VAN", // Assuming "Vancouver Whitecaps FC" might be truncated
    "Austin FC": "AUS",
    "Nashville SC": "NSH",
    "Philadelphia Union": "PHI",
    "San Jose Earthquakes": "SJE",
    "Minnesota United": "MIN", // Assuming "Minnesota United FC" might be truncated
    // --- END UPDATED MLS Teams ---

    // --- La Liga (keys = EXACT StatsBomb names from match_schedule, verified 2026-07-17;
    //     includes 26/27 promoted Málaga/Racing/Depor and 25/26 clubs for historical renders) ---
    "Athletic Club": "ATH",
    "Atlético Madrid": "ATM",
    "Barcelona": "BAR",
    "Celta Vigo": "CEL",
    "Deportivo Alavés": "ALA",
    "Elche": "ELC",
    "Espanyol": "ESP",
    "Getafe": "GET",
    "Girona": "GIR",
    "Levante UD": "LEV",
    "Málaga": "MLG",
    "Mallorca": "MLL",
    "Osasuna": "OSA",
    "Racing Santander": "RAC",
    "Rayo Vallecano": "RAY",
    "RC Deportivo La Coruña": "DEP",
    "Real Betis": "BET",
    "Real Madrid": "RMA",
    "Real Oviedo": "OVI",
    "Real Sociedad": "RSO",
    "Sevilla": "SEV",
    "Valencia": "VAL",
    "Villarreal": "VIL",
    // --- END La Liga ---
};

// --- NEW: League Sizes (Keep this as you had it, ensuring MLS is 30) ---
export const leagueSizes: { [leagueShortName: string]: number } = {
    "EPL": 20,
    "MLS": 30,      // dead competition; kept for historical renders
    "La Liga": 20,  // keyed by Competition.short_name verbatim
};
// --- END NEW ---

/**
 * Gets the abbreviation for a single team name.
 * Falls back to the original name if no abbreviation is found.
 */
export const getAbbreviation = (teamName: string | null | undefined): string => {
    if (!teamName) return 'N/A';
    // First, try an exact match
    if (teamAbbreviations[teamName]) {
        return teamAbbreviations[teamName];
    }
    // Fallback: if StatsBomb sends "TeamName FC" but your map just has "TeamName"
    // this is a basic attempt to strip common suffixes. You might need more robust logic
    // if naming is very inconsistent.
    const commonSuffixes = [" FC", " SC", " CF"];
    for (const suffix of commonSuffixes) {
        if (teamName.endsWith(suffix)) {
            const baseName = teamName.substring(0, teamName.length - suffix.length);
            if (teamAbbreviations[baseName]) {
                return teamAbbreviations[baseName];
            }
        }
    }
    // If still no match, return the original name or a truncated version if too long
    return teamName.length > 15 ? teamName.substring(0, 12) + "..." : teamName;
};

/**
 * Formats the matchup string using abbreviations.
 * Parses opponent strings like "v Team Name", "vs. Team Name", or "@ Team Name".
 */
export const formatMatchupAbbreviation = (
    playerTeamName: string | null | undefined,
    opponentString: string | null | undefined
): string => {
    const playerTeamAbbr = getAbbreviation(playerTeamName);

    if (!opponentString) {
        return playerTeamAbbr;
    }

    let separator = 'vs';
    let opponentFullName = opponentString.trim();
    const versusMatch = opponentFullName.match(/^v(?:s)?\.?\s+(.+)$/i);
    const awayMatch = opponentFullName.match(/^@\s*(.+)$/);

    if (versusMatch) {
        opponentFullName = versusMatch[1];
    } else if (awayMatch) {
        opponentFullName = awayMatch[1];
        separator = '@';
    }

    const opponentAbbr = getAbbreviation(opponentFullName);

    return `${playerTeamAbbr} ${separator} ${opponentAbbr}`;
};
